import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	LineElement,
	PointElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { IMonthlySeriesItem } from '../../types';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

interface MonthlySeriesChartProps {
	data: IMonthlySeriesItem[] | null;
	loading: boolean;
	sellerId?: number | string;
}

function formatMonth(month: string): string {
	const [year, monthNum] = month.split('-');
	const date = new Date(Number(year), Number(monthNum) - 1, 1);
	return date.toLocaleDateString('es', { month: 'short', year: '2-digit' });
}

function formatUSDShort(value: number): string {
	if (Math.abs(value) >= 1_000_000) return `USD ${(value / 1_000_000).toFixed(1)}M`;
	if (Math.abs(value) >= 1_000) return `USD ${(value / 1_000).toFixed(1)}k`;
	return `USD ${value.toFixed(0)}`;
}

export function MonthlySeriesChart({ data, loading, sellerId }: MonthlySeriesChartProps): JSX.Element {
	if (loading) {
		return <Skeleton variant="rectangular" height={280} />;
	}

	if (!data || data.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
				Sin datos de evolución mensual
			</Typography>
		);
	}

	// Si hay sellerId → filtrar por ese vendedor.
	// Si no hay sellerId (vista equipo) → agregar todos los vendedores por mes para mostrar el total.
	const preFiltered = sellerId
		? data.filter((item) => String(item.seller_id) === String(sellerId))
		: (() => {
				const byMonth = new Map<string, { sale: number; gp: number }>();
				for (const item of data) {
					const prev = byMonth.get(item.month) ?? { sale: 0, gp: 0 };
					byMonth.set(item.month, {
						sale: prev.sale + Number(item.sale_amount),
						gp: prev.gp + Number(item.gross_profit),
					});
				}
				return Array.from(byMonth.entries()).map(([month, vals]) => ({
					...data[0],
					seller_id: '',
					seller_name: 'Equipo',
					month,
					sale_amount: vals.sale,
					gross_profit: vals.gp,
				}));
		  })();

	const relevantItems = preFiltered
		.slice()
		.sort((a, b) => a.month.localeCompare(b.month))
		.slice(-12);

	if (relevantItems.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
				Sin datos de evolución mensual para este vendedor
			</Typography>
		);
	}

	const labels = relevantItems.map((item) => formatMonth(item.month));
	const saleAmounts = relevantItems.map((item) => Number(item.sale_amount));
	const grossProfits = relevantItems.map((item) => Number(item.gross_profit));

	const chartData = {
		labels,
		datasets: [
			{
				label: 'Venta',
				data: saleAmounts,
				backgroundColor: 'rgba(237, 102, 2, 0.7)',
				borderRadius: 4,
				order: 2,
			},
			{
				label: 'Margen bruto',
				data: grossProfits,
				backgroundColor: 'rgba(49, 25, 75, 0.7)',
				borderRadius: 4,
				order: 1,
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { position: 'top' as const },
			tooltip: {
				callbacks: {
					label: (ctx: { dataset: { label?: string }; parsed: { y: number } }) =>
						`${ctx.dataset.label}: ${formatUSDShort(ctx.parsed.y)}`,
				},
			},
		},
		scales: {
			y: {
				ticks: {
					callback: (value: number | string) => (typeof value === 'number' ? formatUSDShort(value) : value),
				},
			},
		},
	};

	return (
		<Box sx={{ height: 280, position: 'relative' }}>
			<Bar data={chartData} options={options} />
		</Box>
	);
}
